import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate } from "@/lib/format";
import BranchFilter from "@/components/BranchFilter";
import DateRangeFilter from "@/components/DateRangeFilter";
import useBranches from "@/hooks/useBranches";
import { Plus, MagnifyingGlass, Trash, X, CheckCircle, Printer, ArrowUUpLeft, ArrowFatRight } from "@phosphor-icons/react";
import { printAdvanceReceipt, printRefundVoucher } from "./serviceAdvanceReceipt";
import PartPicker from "@/components/PartPicker";

/**
 * Service Advances — customer prepayments against a future service invoice.
 * Three flavours (backend TYPES): battery_warranty | accident_engine | direct_parts.
 * Auto-links to a Job Card when a JC # is entered.
 */
const TYPE_META = {
  battery_warranty: { label: "Battery Warranty", cls: "border-[#5f3dc4] text-[#5f3dc4]" },
  accident_engine: { label: "Accident / Engine", cls: "border-[#c92a2a] text-[#c92a2a]" },
  direct_parts: { label: "Direct Parts", cls: "border-[#00713a] text-[#00713a]" },
};

const TYPE_TABS = [
  { id: "", label: "All Types" },
  { id: "battery_warranty", label: "Battery Warranty" },
  { id: "accident_engine", label: "Accident / Engine" },
  { id: "direct_parts", label: "Direct Parts" },
];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function ServiceAdvancesTab({ canCreate, canEdit, canDelete }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchId, setBranchId] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(today); start.setDate(today.getDate() - 29);
    return { from: ymd(start), to: ymd(today) };
  });
  const [drawer, setDrawer] = useState({ open: false, editing: null });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (typeFilter) params.advance_type = typeFilter;
      if (status) params.status = status;
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/service-advances", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, typeFilter, status, dateRange.from, dateRange.to]);

  const kpis = useMemo(() => rows.reduce((acc, r) => ({
    count: acc.count + 1,
    total: acc.total + (Number(r.amount) || 0),
    open: acc.open + (r.status === "open" ? 1 : 0),
    remaining: acc.remaining + (Number(r.remaining) || 0),
  }), { count: 0, total: 0, open: 0, remaining: 0 }), [rows]);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete advance ${row.advance_no}?`)) return;
    try {
      await api.delete(`/service-advances/${row.id}`);
      toast.success("Advance deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <div className="inline-flex border border-canvas-line" data-testid="adv-type-tabs">
        {TYPE_TABS.map((t, i) => (
          <button key={t.id || "all"} type="button" onClick={() => setTypeFilter(t.id)}
            data-testid={`adv-type-${t.id || "all"}`}
            className={`px-3 py-2 text-[11px] font-mono uppercase tracking-wider ${i > 0 ? "border-l border-canvas-line" : ""} ${typeFilter === t.id ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <BranchFilter value={branchId} onChange={setBranchId} testId="adv-branch-filter" kind="service" />
        <DateRangeFilter value={dateRange} onChange={setDateRange} testIdPrefix="adv-date" />
        <div className="flex items-center border border-canvas-line bg-canvas">
          <MagnifyingGlass size={12} weight="bold" className="mx-2 text-ink-muted" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search advance / customer / JC / vehicle"
            data-testid="adv-search"
            className="px-1 py-2 bg-canvas text-sm focus:outline-none w-64" />
        </div>
        <div className="inline-flex border border-canvas-line">
          {[["", "All"], ["open", "Open"], ["adjusted", "Adjusted"], ["refunded", "Refunded"]].map(([v, l], i) => (
            <button key={v || "all"} type="button" onClick={() => setStatus(v)}
              data-testid={`adv-status-${v || "all"}`}
              className={`px-3 py-2 text-[11px] font-mono uppercase tracking-wider ${i > 0 ? "border-l border-canvas-line" : ""} ${status === v ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}>
              {l}
            </button>
          ))}
        </div>
        {canCreate && (
          <button type="button" onClick={() => setDrawer({ open: true, editing: null })}
            data-testid="new-advance-button"
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-ink/90">
            <Plus size={12} weight="bold" /> New Advance
          </button>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2">
        <Kpi label="Advances" value={kpis.count} />
        <Kpi label="Open" value={kpis.open} />
        <Kpi label="Collected ₹" value={fmtMoney(kpis.total)} />
        <Kpi label="Outstanding ₹" value={fmtMoney(kpis.remaining)} />
      </div>

      <div className="border border-canvas-line bg-canvas-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="advances-table">
          <thead className="bg-canvas-panel">
            <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">Advance #</th>
              <th className="px-3 py-2.5">Type</th>
              <th className="px-3 py-2.5">Customer / Vehicle</th>
              <th className="px-3 py-2.5">JC #</th>
              <th className="px-3 py-2.5 text-right">Amount ₹</th>
              <th className="px-3 py-2.5 text-right">Adjusted</th>
              <th className="px-3 py-2.5 text-right">Remaining</th>
              <th className="px-3 py-2.5">Pay</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">
                No advances in this window. Create the first one from the button above.
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const meta = TYPE_META[r.advance_type] || {};
              return (
                <tr key={r.id} data-testid={`adv-row-${r.id}`}
                    onClick={() => setDrawer({ open: true, editing: r })}
                    className="border-t border-canvas-line hover:bg-canvas-panel/40 cursor-pointer">
                  <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.advance_no}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${meta.cls || ""}`}>
                      {meta.label || r.advance_type}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{r.customer_name || "—"}</div>
                    <div className="text-xs text-ink-muted">{r.vehicle_reg_no || ""}</div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.jc_no || "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-bold">{fmtMoney(r.amount)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-xs">{fmtMoney(r.adjusted_amount)}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${r.remaining > 0 ? "text-[#A35700]" : "text-ink-subtle"}`}>
                    {fmtMoney(r.remaining)}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${
                      r.payment_method === "cash" ? "border-[#00713a] text-[#00713a]" : "border-blue-500 text-blue-700"
                    }`}>{r.payment_method}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${
                      r.status === "adjusted" ? "border-[#00713a] text-[#00713a]" :
                      r.status === "refunded" ? "border-canvas-line text-ink-subtle" :
                      "border-ink text-ink"
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    {canDelete && r.status === "open" && Number(r.adjusted_amount) === 0 && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(r); }}
                        data-testid={`delete-adv-${r.id}`}
                        className="p-1.5 border border-canvas-line hover:border-destructive text-destructive" title="Delete">
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {drawer.open && (
        <AdvanceDrawer editing={drawer.editing} canEdit={canEdit}
          onClose={() => setDrawer({ open: false, editing: null })}
          onSaved={() => { setDrawer({ open: false, editing: null }); load(); }} />
      )}
    </>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-canvas-line bg-canvas-card p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className="font-display text-lg font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function AdvanceDrawer({ editing, canEdit, onClose, onSaved }) {
  const { branches: serviceBranches } = useBranches({ activeOnly: true, kind: "service" });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(editing);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    branch_id: "",
    advance_type: "battery_warranty",
    customer_name: "", customer_phone: "",
    vehicle_reg_no: "",
    jc_no: "",
    amount: 0,
    payment_method: "cash",
    upi_ref: "",
    reason: "",
    notes: "",
    old_battery_no: "",
    temp_battery_no: "",
    parts_lines: [],
  });
  const [adjust, setAdjust] = useState({ open: false, amount: 0, linked_invoice_no: "", note: "" });
  const [refundModal, setRefundModal] = useState({ open: false, amount: 0, method: "cash", ref: "", note: "" });
  const [batteryEdit, setBatteryEdit] = useState({ open: false, warranty_battery_no: "", warranty_received_at: "", saving: false });
  // Parts management (direct_parts / accident_engine only)
  const [newPartLine, setNewPartLine] = useState({ part_code: "", description: "", hsn_code: "", qty: 1, unit_price: 0, notes: "" });
  const isPartsType = (t) => t === "direct_parts" || t === "accident_engine";

  const isEdit = !!editing;

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/service-advances/${editing.id}`);
        setDetail(data);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [editing]);

  useEffect(() => {
    if (!editing && serviceBranches.length === 1 && !form.branch_id) {
      setForm((f) => ({ ...f, branch_id: serviceBranches[0].id }));
    }
  }, [serviceBranches, editing, form.branch_id]);

  const onSaveNew = async () => {
    if (!form.branch_id) { toast.error("Pick a branch."); return; }
    if (!form.customer_name.trim()) { toast.error("Customer name is required."); return; }
    if (Number(form.amount) <= 0) { toast.error("Amount must be > 0."); return; }
    if (form.payment_method === "upi" && !form.upi_ref.trim()) { toast.error("UPI requires a UTR."); return; }
    setSaving(true);
    try {
      await api.post("/service-advances", { ...form, amount: Number(form.amount) });
      toast.success("Advance recorded");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const submitAdjust = async () => {
    if (Number(adjust.amount) <= 0) { toast.error("Amount must be > 0."); return; }
    try {
      const { data } = await api.post(`/service-advances/${detail.id}/adjust`, {
        amount: Number(adjust.amount),
        linked_invoice_no: adjust.linked_invoice_no,
        note: adjust.note,
      });
      toast.success(`Adjusted ₹${adjust.amount} · Remaining ₹${data.remaining}`);
      setDetail(data);
      setAdjust({ open: false, amount: 0, linked_invoice_no: "", note: "" });
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitRefund = async () => {
    const r = refundModal;
    if (Number(r.amount) <= 0) { toast.error("Refund amount must be > 0."); return; }
    if (Number(r.amount) > Number(detail.remaining) + 0.01) {
      toast.error(`Refund exceeds remaining ₹${detail.remaining}.`); return;
    }
    try {
      const { data } = await api.patch(`/service-advances/${detail.id}`, {
        status: "refunded",
        refund_amount: Number(r.amount),
        refund_note: r.note,
      });
      setDetail(data);
      toast.success(`Cash refund posted · Voucher ${data.refund_voucher_no || ""}`);
      setRefundModal({ open: false, amount: 0, method: "cash", ref: "", note: "" });
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveBatteryReceipt = async () => {
    const b = batteryEdit;
    if (!b.warranty_battery_no.trim()) { toast.error("Warranty battery # is required."); return; }
    setBatteryEdit((x) => ({ ...x, saving: true }));
    try {
      const { data } = await api.patch(`/service-advances/${detail.id}`, {
        warranty_battery_no: b.warranty_battery_no.trim(),
        warranty_received_at: (b.warranty_received_at || new Date().toISOString().slice(0, 10)),
      });
      setDetail(data);
      toast.success("Warranty replacement recorded");
      setBatteryEdit({ open: false, warranty_battery_no: "", warranty_received_at: "", saving: false });
      onSaved();
    } catch (err) {
      setBatteryEdit((x) => ({ ...x, saving: false }));
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-[560px] bg-canvas-card border-l-2 border-ink overflow-y-auto"
        data-testid="advance-drawer">
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between sticky top-0 bg-canvas-card z-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              {isEdit ? detail?.advance_no : "New Service Advance"}
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">
              {isEdit ? (detail?.customer_name || "—") : "Collect Advance"}
              {isEdit && detail?.status && (
                <span className={`ml-2 inline-block px-2 py-0.5 border text-[10px] font-mono uppercase align-middle ${
                  detail.status === "adjusted" ? "border-[#00713a] text-[#00713a]" :
                  detail.status === "refunded" ? "border-canvas-line text-ink-subtle" :
                  "border-ink text-ink"
                }`}>{detail.status}</span>
              )}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!isEdit && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <F label="Date" type="date" value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} />
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Branch *</label>
                  <select value={form.branch_id} onChange={(e) => setForm((f) => ({ ...f, branch_id: e.target.value }))}
                    data-testid="adv-branch"
                    className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink">
                    <option value="">— select service branch —</option>
                    {serviceBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Advance Type *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(TYPE_META).map(([v, m]) => (
                      <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, advance_type: v }))}
                        data-testid={`adv-type-btn-${v}`}
                        className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border ${
                          form.advance_type === v ? `${m.cls} bg-canvas-panel border-2` : "border-canvas-line text-ink-muted hover:border-ink"
                        }`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <F label="Customer *" value={form.customer_name} onChange={(v) => setForm((f) => ({ ...f, customer_name: v }))} />
                <F label="Phone" value={form.customer_phone} onChange={(v) => setForm((f) => ({ ...f, customer_phone: v }))} mono />
                <F label="Vehicle Reg #" value={form.vehicle_reg_no} onChange={(v) => setForm((f) => ({ ...f, vehicle_reg_no: v }))} mono />
                <F label="JC # (optional — auto-links)" value={form.jc_no} onChange={(v) => setForm((f) => ({ ...f, jc_no: v }))} mono />
                <F label="Amount ₹ *" type="number" value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} />
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Payment</label>
                  <select value={form.payment_method} onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))}
                    className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink">
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                  </select>
                </div>
                {form.payment_method === "upi" && (
                  <F label="UTR / Ref *" value={form.upi_ref} onChange={(v) => setForm((f) => ({ ...f, upi_ref: v }))} mono />
                )}
              </div>
              {form.advance_type === "battery_warranty" && (
                <div className="border border-canvas-line p-3 bg-canvas-panel/20 space-y-2" data-testid="battery-fields">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Battery Tracking</div>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Old Battery #" value={form.old_battery_no} onChange={(v) => setForm((f) => ({ ...f, old_battery_no: v }))} mono />
                    <F label="Temp / Stand-by Battery #" value={form.temp_battery_no} onChange={(v) => setForm((f) => ({ ...f, temp_battery_no: v }))} mono />
                  </div>
                  <div className="text-[10px] text-ink-muted font-mono">
                    Old battery is being sent to OEM for warranty. A stand-by is loaned to the customer. Warranty replacement gets recorded later.
                  </div>
                </div>
              )}
              {isPartsType(form.advance_type) && (
                <NewAdvancePartsEditor
                  lines={form.parts_lines}
                  setLines={(next) => setForm((f) => ({ ...f, parts_lines: next }))} />
              )}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Reason / Details</label>
                <textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  rows={2} placeholder="e.g., Battery slab replacement / accident brief / part codes ordered"
                  className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink" />
              </div>
              <div className="pt-3 border-t border-canvas-line flex justify-end gap-2">
                <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">Cancel</button>
                <button type="button" onClick={onSaveNew} disabled={saving}
                  data-testid="save-advance"
                  className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-ink/90 disabled:opacity-60">
                  {saving ? "Saving…" : "Collect Advance"}
                </button>
              </div>
            </>
          )}

          {isEdit && detail && (
            <>
              <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-canvas-line">
                <button type="button" onClick={() => printAdvanceReceipt(detail)}
                  data-testid="print-advance-receipt"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-canvas-line hover:border-ink text-xs font-mono uppercase tracking-wider">
                  <Printer size={12} weight="bold" /> Advance Receipt
                </button>
                {detail.status === "refunded" && detail.refund_voucher_no && (
                  <button type="button" onClick={() => printRefundVoucher(detail)}
                    data-testid="print-refund-voucher"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#a11] text-[#a11] hover:bg-[#a11] hover:text-white text-xs font-mono uppercase tracking-wider">
                    <Printer size={12} weight="bold" /> Refund Voucher
                  </button>
                )}
                {detail.refund_voucher_no && (
                  <span className="ml-auto font-mono text-[10px] text-ink-muted">
                    PV: <b className="text-ink">{detail.refund_voucher_no}</b>
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3 border border-canvas-line p-3 bg-canvas-panel/30">
                <Stat label="Amount" value={fmtMoney(detail.amount)} />
                <Stat label="Adjusted" value={fmtMoney(detail.adjusted_amount)} />
                <Stat label="Remaining" value={fmtMoney(detail.remaining)} accent={detail.remaining > 0 ? "text-[#A35700]" : "text-ink-subtle"} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <M label="Advance" value={detail.advance_no} />
                <M label="Receipt" value={detail.receipt_no} />
                <M label="Type" value={(TYPE_META[detail.advance_type] || {}).label} />
                <M label="Date" value={fmtDate(detail.date)} />
                <M label="Vehicle" value={detail.vehicle_reg_no} />
                <M label="Phone" value={detail.customer_phone} />
                <M label="JC #" value={detail.jc_no} />
                <M label="Payment" value={`${detail.payment_method}${detail.upi_ref ? " · " + detail.upi_ref : ""}`} />
                <M label="Reason" value={detail.reason} colspan={2} />
              </div>

              {detail.advance_type === "battery_warranty" && (
                <div className="border border-canvas-line p-3 bg-canvas-panel/20 space-y-2" data-testid="battery-track-block">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Battery Tracking</div>
                    {canEdit && !detail.warranty_battery_no && detail.status !== "refunded" && (
                      <button type="button" onClick={() => setBatteryEdit({ open: true, warranty_battery_no: "", warranty_received_at: new Date().toISOString().slice(0, 10), saving: false })}
                        data-testid="record-warranty-battery"
                        className="inline-flex items-center gap-1 px-2 py-1 border border-canvas-line hover:border-ink text-[10px] font-mono uppercase">
                        <Plus size={10} weight="bold" /> Record warranty battery
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <M label="Old battery #" value={detail.old_battery_no} />
                    <M label="Temp / Stand-by #" value={detail.temp_battery_no} />
                    <M label="Warranty battery #" value={detail.warranty_battery_no} />
                    <M label="Warranty received" value={detail.warranty_received_at ? fmtDate(detail.warranty_received_at) : ""} />
                  </div>
                  {batteryEdit.open && (
                    <div className="border-t border-canvas-line pt-2 space-y-2" data-testid="battery-edit-form">
                      <div className="grid grid-cols-2 gap-2">
                        <F label="Warranty Battery # *" value={batteryEdit.warranty_battery_no} onChange={(v) => setBatteryEdit((x) => ({ ...x, warranty_battery_no: v }))} mono />
                        <F label="Received Date" type="date" value={batteryEdit.warranty_received_at} onChange={(v) => setBatteryEdit((x) => ({ ...x, warranty_received_at: v }))} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setBatteryEdit({ open: false, warranty_battery_no: "", warranty_received_at: "", saving: false })} className="px-3 py-1.5 border border-canvas-line text-xs">Cancel</button>
                        <button type="button" onClick={saveBatteryReceipt} disabled={batteryEdit.saving}
                          data-testid="save-warranty-battery"
                          className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-ink/90 disabled:opacity-60">
                          {batteryEdit.saving ? "Saving…" : "Record replacement"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isPartsType(detail.advance_type) && (
                <EditPartsBlock detail={detail} canEdit={canEdit}
                  newLine={newPartLine} setNewLine={setNewPartLine}
                  onChanged={(next) => { setDetail(next); onSaved(); }} />
              )}

              {detail.status === "refunded" && (
                <div className="border border-[#a11]/40 bg-[#a11]/5 p-3 space-y-1" data-testid="refund-summary">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a11]">Refund Details</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <M label="Refunded amount" value={fmtMoney(detail.refund_amount)} />
                    <M label="Method" value={`${detail.refund_method || ""}${detail.refund_ref ? " · " + detail.refund_ref : ""}`} />
                    <M label="Refunded on" value={detail.refunded_at ? fmtDate(detail.refunded_at.slice(0, 10)) : ""} />
                    <M label="Voucher #" value={detail.refund_voucher_no} />
                    <M label="Note" value={detail.refund_note} colspan={2} />
                  </div>
                </div>
              )}

              <div className="border border-canvas-line">
                <div className="px-3 py-2 bg-canvas-panel flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                    Adjustments ({(detail.adjustments || []).length})
                  </div>
                  {canEdit && detail.status !== "refunded" && detail.remaining > 0 && (
                    <button type="button" onClick={() => setAdjust((a) => ({ ...a, open: !a.open }))}
                      data-testid="adjust-toggle"
                      className="inline-flex items-center gap-1 px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase hover:border-ink">
                      <Plus size={10} weight="bold" /> Record adjustment
                    </button>
                  )}
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-canvas-panel/50">
                    <tr className="text-left font-mono text-[9px] uppercase tracking-wider text-ink-muted">
                      <th className="px-2 py-1.5">Date</th>
                      <th className="px-2 py-1.5">Invoice</th>
                      <th className="px-2 py-1.5">Note</th>
                      <th className="px-2 py-1.5 text-right">₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.adjustments || []).map((a) => (
                      <tr key={a.id} className="border-t border-canvas-line">
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(a.date)}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{a.linked_invoice_no || "—"}</td>
                        <td className="px-2 py-1.5 text-[10px] text-ink-muted">{a.note || ""}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold">{fmtMoney(a.amount)}</td>
                      </tr>
                    ))}
                    {(detail.adjustments || []).length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-4 text-center text-ink-subtle font-mono text-[10px]">
                        No adjustments yet.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {adjust.open && (
                <div className="border border-canvas-line p-3 space-y-2 bg-canvas-panel/20">
                  <div className="grid grid-cols-2 gap-2">
                    <F label="Amount ₹ *" type="number" value={adjust.amount} onChange={(v) => setAdjust((a) => ({ ...a, amount: v }))} />
                    <F label="Linked Invoice #" value={adjust.linked_invoice_no} onChange={(v) => setAdjust((a) => ({ ...a, linked_invoice_no: v }))} mono />
                  </div>
                  <F label="Note" value={adjust.note} onChange={(v) => setAdjust((a) => ({ ...a, note: v }))} />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setAdjust((a) => ({ ...a, open: false }))} className="px-3 py-1.5 border border-canvas-line text-xs">Cancel</button>
                    <button type="button" onClick={submitAdjust}
                      data-testid="save-adjust"
                      className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-ink/90">
                      Record adjustment
                    </button>
                  </div>
                </div>
              )}

              {canEdit && detail.status !== "refunded" && detail.remaining > 0 && (
                <button type="button" onClick={() => setRefundModal({ open: true, amount: detail.remaining, method: "cash", ref: "", note: "" })}
                  data-testid="mark-refunded"
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-destructive text-destructive text-sm hover:bg-destructive hover:text-white">
                  <ArrowUUpLeft size={14} weight="bold" /> Process cash refund (₹{detail.remaining} to customer)
                </button>
              )}
              {refundModal.open && (
                <div className="border border-destructive p-3 space-y-2 bg-canvas-panel/20" data-testid="refund-modal">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-destructive">Cash Refund</div>
                  <div className="grid grid-cols-2 gap-2">
                    <F label="Refund Amount ₹ *" type="number" value={refundModal.amount} onChange={(v) => setRefundModal((r) => ({ ...r, amount: v }))} />
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Method</label>
                      <div className="px-3 py-2 border border-canvas-line bg-canvas-panel/40 text-sm font-mono" data-testid="refund-method-locked">CASH · locked</div>
                    </div>
                  </div>
                  <F label="Refund Note" value={refundModal.note} onChange={(v) => setRefundModal((r) => ({ ...r, note: v }))} />
                  <div className="text-[10px] text-ink-muted font-mono">
                    A cash Payment Voucher (PV) will be auto-posted to Cashbook against this refund.
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setRefundModal({ open: false, amount: 0, method: "cash", ref: "", note: "" })} className="px-3 py-1.5 border border-canvas-line text-xs">Cancel</button>
                    <button type="button" onClick={submitRefund}
                      data-testid="confirm-refund"
                      className="px-3 py-1.5 bg-destructive text-white text-xs font-semibold hover:bg-destructive/90">
                      Post cash refund & voucher
                    </button>
                  </div>
                </div>
              )}
              {detail.status === "adjusted" && (
                <div className="inline-flex items-center gap-2 px-3 py-2 border border-[#00713a] text-[#00713a] text-sm w-full justify-center">
                  <CheckCircle size={14} weight="bold" /> Fully adjusted
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", mono }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink ${mono ? "font-mono" : ""}`} />
    </div>
  );
}
function M({ label, value, colspan }) {
  return (
    <div className={colspan === 2 ? "col-span-2" : ""}>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-subtle">{label}</div>
      <div className="text-sm">{value || <span className="text-ink-subtle">—</span>}</div>
    </div>
  );
}
function Stat({ label, value, accent = "" }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-subtle">{label}</div>
      <div className={`font-display text-lg font-bold tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}


/* ----------------- New-Advance Parts Editor ----------------- */
const emptyPartLine = () => ({
  part_code: "", description: "", hsn_code: "", qty: 1, unit_price: 0, tax_percent: 0, notes: "",
  order_date: new Date().toISOString().slice(0, 10),
  order_type: "APS",
  order_number: "",
  expected_delivery_date: "",
});

const ORDER_TYPES = ["APS", "VOR", "DEALER"];

function NewAdvancePartsEditor({ lines, setLines }) {
  const [row, setRow] = useState(emptyPartLine());

  const addRow = () => {
    if (!row.part_code.trim()) { toast.error("Pick a part."); return; }
    if (Number(row.qty) <= 0) { toast.error("Qty must be > 0."); return; }
    setLines([...(lines || []), { ...row, qty: Number(row.qty), unit_price: Number(row.unit_price) }]);
    setRow(emptyPartLine());
  };

  const removeAt = (i) => {
    const next = [...lines]; next.splice(i, 1); setLines(next);
  };

  return (
    <div className="border border-canvas-line p-3 bg-canvas-panel/20 space-y-2" data-testid="parts-editor-new">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Required Parts (customer-earmarked)</div>
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-4">
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Part *</label>
          <PartPicker
            value={row.part_code}
            onSelect={(p) => setRow((r) => ({
              ...r, part_code: p.part_code, description: p.description || "", hsn_code: p.hsn_code || "",
              unit_price: p.unit_price || 0, tax_percent: p.tax_percent || 0,
            }))}
            onTypedValue={(v) => setRow((r) => ({ ...r, part_code: v }))}
            testId="new-part-picker" />
        </div>
        <div className="col-span-2">
          <F label="Qty *" type="number" value={row.qty} onChange={(v) => setRow((r) => ({ ...r, qty: v }))} />
        </div>
        <div className="col-span-3">
          <F label="Unit ₹" type="number" value={row.unit_price} onChange={(v) => setRow((r) => ({ ...r, unit_price: v }))} />
        </div>
        <div className="col-span-3">
          <button type="button" onClick={addRow}
            data-testid="add-part-line"
            className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 border border-ink text-ink text-xs font-mono uppercase hover:bg-ink hover:text-white">
            <Plus size={10} weight="bold" /> Add line
          </button>
        </div>
        <div className="col-span-3">
          <F label="Order Date" type="date" value={row.order_date} onChange={(v) => setRow((r) => ({ ...r, order_date: v }))} />
        </div>
        <div className="col-span-2">
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Order Type</label>
          <select value={row.order_type} onChange={(e) => setRow((r) => ({ ...r, order_type: e.target.value }))}
            data-testid="new-part-order-type"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink">
            {ORDER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="col-span-4">
          <F label="Order #" value={row.order_number} onChange={(v) => setRow((r) => ({ ...r, order_number: v }))} mono />
        </div>
        <div className="col-span-3">
          <F label="Expected Delivery" type="date" value={row.expected_delivery_date} onChange={(v) => setRow((r) => ({ ...r, expected_delivery_date: v }))} />
        </div>
      </div>
      {(lines || []).length > 0 && (
        <table className="w-full text-xs" data-testid="new-parts-list">
          <thead className="bg-canvas-panel/50">
            <tr className="text-left font-mono text-[9px] uppercase tracking-wider text-ink-muted">
              <th className="px-2 py-1.5">Part</th>
              <th className="px-2 py-1.5 text-right">Qty</th>
              <th className="px-2 py-1.5 text-right">Unit ₹</th>
              <th className="px-2 py-1.5">Order</th>
              <th className="px-2 py-1.5">Expected</th>
              <th className="px-2 py-1.5 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((ln, i) => (
              <tr key={i} className="border-t border-canvas-line">
                <td className="px-2 py-1.5">
                  <div className="font-mono">{ln.part_code}</div>
                  <div className="text-[10px] text-ink-muted">{ln.description}</div>
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums font-bold">{ln.qty}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney(ln.unit_price)}</td>
                <td className="px-2 py-1.5">
                  <div className="font-mono text-[10px]">{ln.order_type || "—"} · {ln.order_number || "—"}</div>
                  <div className="text-[10px] text-ink-muted">{ln.order_date || ""}</div>
                </td>
                <td className="px-2 py-1.5 text-[10px] text-ink-muted">{ln.expected_delivery_date || "—"}</td>
                <td className="px-2 py-1.5">
                  <button type="button" onClick={() => removeAt(i)}
                    className="p-1 border border-canvas-line hover:border-destructive text-destructive"
                    title="Remove">
                    <Trash size={10} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="text-[10px] text-ink-muted font-mono">
        These lines feed the Required Order report as a Customer Advance qty and can be tracked per line after saving.
      </div>
    </div>
  );
}

/* ----------------- Edit-view Parts Block ----------------- */
const PART_STATUS_META = {
  ordered: { label: "Ordered", cls: "border-ink text-ink" },
  received: { label: "Received", cls: "border-[#00713a] text-[#00713a]" },
  delivered: { label: "Delivered", cls: "border-canvas-line text-ink-subtle" },
};

function EditPartsBlock({ detail, canEdit, newLine, setNewLine, onChanged }) {
  const [busyId, setBusyId] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingMeta, setEditingMeta] = useState(null); // line_id currently editing order meta
  const [metaDraft, setMetaDraft] = useState({});
  const lines = detail.parts || [];

  const patchLine = async (line_id, patch) => {
    setBusyId(line_id);
    try {
      const { data } = await api.patch(`/service-advances/${detail.id}/parts/${line_id}`, patch);
      onChanged(data);
      toast.success("Line updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusyId(""); }
  };

  const deleteLine = async (line_id) => {
    if (!window.confirm("Remove this line?")) return;
    setBusyId(line_id);
    try {
      const { data } = await api.delete(`/service-advances/${detail.id}/parts/${line_id}`);
      onChanged(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusyId(""); }
  };

  const addLine = async () => {
    if (!newLine.part_code.trim()) { toast.error("Pick a part."); return; }
    if (Number(newLine.qty) <= 0) { toast.error("Qty must be > 0."); return; }
    setAdding(true);
    try {
      const { data } = await api.post(`/service-advances/${detail.id}/parts`, {
        ...newLine, qty: Number(newLine.qty), unit_price: Number(newLine.unit_price),
      });
      onChanged(data);
      setNewLine(emptyPartLine());
      toast.success("Part added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setAdding(false); }
  };

  const startMetaEdit = (ln) => {
    setEditingMeta(ln.id);
    setMetaDraft({
      order_date: ln.order_date || "",
      order_type: ln.order_type || "APS",
      order_number: ln.order_number || "",
      expected_delivery_date: ln.expected_delivery_date || "",
    });
  };
  const saveMeta = async (line_id) => {
    await patchLine(line_id, { ...metaDraft });
    setEditingMeta(null);
  };

  const openCount = lines.filter((l) => l.status !== "delivered").length;
  const deliveredCount = lines.length - openCount;

  return (
    <div className="border border-canvas-line" data-testid="parts-block">
      <div className="px-3 py-2 bg-canvas-panel flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
          Required Parts ({lines.length}) · Open {openCount} · Delivered {deliveredCount}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="parts-table">
          <thead className="bg-canvas-panel/50">
            <tr className="text-left font-mono text-[9px] uppercase tracking-wider text-ink-muted">
              <th className="px-2 py-1.5">Part</th>
              <th className="px-2 py-1.5 text-right">Qty</th>
              <th className="px-2 py-1.5 text-right">Unit ₹</th>
              <th className="px-2 py-1.5">Order</th>
              <th className="px-2 py-1.5">Expected</th>
              <th className="px-2 py-1.5">Status</th>
              <th className="px-2 py-1.5 w-52"></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={7} className="px-2 py-4 text-center text-ink-subtle font-mono text-[10px]">
                No parts yet. Add customer-earmarked parts below.
              </td></tr>
            )}
            {lines.map((ln) => {
              const meta = PART_STATUS_META[ln.status] || {};
              const isEditingThis = editingMeta === ln.id;
              return (
                <tr key={ln.id} className="border-t border-canvas-line align-top" data-testid={`part-row-${ln.id}`}>
                  <td className="px-2 py-1.5">
                    <div className="font-mono">{ln.part_code}</div>
                    <div className="text-[10px] text-ink-muted">{ln.description || ""}</div>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold">{ln.qty}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney(ln.unit_price)}</td>
                  <td className="px-2 py-1.5" data-testid={`part-order-${ln.id}`}>
                    {isEditingThis ? (
                      <div className="space-y-1">
                        <select value={metaDraft.order_type} onChange={(e) => setMetaDraft((m) => ({ ...m, order_type: e.target.value }))}
                          data-testid={`meta-type-${ln.id}`}
                          className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-[10px] focus:outline-none">
                          {ORDER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <input type="text" value={metaDraft.order_number} onChange={(e) => setMetaDraft((m) => ({ ...m, order_number: e.target.value }))}
                          placeholder="Order #"
                          data-testid={`meta-no-${ln.id}`}
                          className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-[10px] font-mono focus:outline-none" />
                        <input type="date" value={metaDraft.order_date} onChange={(e) => setMetaDraft((m) => ({ ...m, order_date: e.target.value }))}
                          data-testid={`meta-date-${ln.id}`}
                          className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-[10px] focus:outline-none" />
                      </div>
                    ) : (
                      <>
                        <div className="font-mono text-[10px]">
                          <span className={`inline-block px-1 py-0 border mr-1 ${ln.order_type === "VOR" ? "border-[#c92a2a] text-[#c92a2a]" : ln.order_type === "APS" ? "border-[#00713a] text-[#00713a]" : ln.order_type ? "border-ink text-ink" : "border-canvas-line text-ink-subtle"}`}>
                            {ln.order_type || "—"}
                          </span>
                          {ln.order_number || <span className="text-ink-subtle">—</span>}
                        </div>
                        <div className="text-[10px] text-ink-muted">{ln.order_date || ""}</div>
                      </>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {isEditingThis ? (
                      <input type="date" value={metaDraft.expected_delivery_date}
                        onChange={(e) => setMetaDraft((m) => ({ ...m, expected_delivery_date: e.target.value }))}
                        data-testid={`meta-exp-${ln.id}`}
                        className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-[10px] focus:outline-none" />
                    ) : (
                      <div className="text-[10px] text-ink-muted">{ln.expected_delivery_date || "—"}</div>
                    )}
                    {ln.received_at && <div className="text-[10px] text-[#00713a]">Recv: {fmtDate(ln.received_at.slice(0, 10))}</div>}
                    {ln.delivered_at && <div className="text-[10px] text-ink">Deliv: {fmtDate(ln.delivered_at.slice(0, 10))}</div>}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${meta.cls || ""}`}>
                      {meta.label || ln.status}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    {isEditingThis ? (
                      <div className="flex gap-1">
                        <button type="button" onClick={() => saveMeta(ln.id)} disabled={busyId === ln.id}
                          data-testid={`meta-save-${ln.id}`}
                          className="px-2 py-1 border border-ink text-ink text-[10px] font-mono uppercase hover:bg-ink hover:text-white disabled:opacity-50">Save</button>
                        <button type="button" onClick={() => setEditingMeta(null)}
                          className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        {canEdit && ln.status !== "delivered" && (
                          <button type="button" onClick={() => startMetaEdit(ln)}
                            data-testid={`meta-edit-${ln.id}`}
                            className="px-2 py-1 border border-canvas-line hover:border-ink text-[10px] font-mono uppercase" title="Edit order meta">
                            Edit meta
                          </button>
                        )}
                        {canEdit && ln.status === "ordered" && (
                          <button type="button" onClick={() => patchLine(ln.id, { status: "received" })} disabled={busyId === ln.id}
                            data-testid={`part-receive-${ln.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-[#00713a] text-[#00713a] text-[10px] font-mono uppercase hover:bg-[#00713a] hover:text-white disabled:opacity-50">
                            <ArrowFatRight size={10} weight="bold" /> Received
                          </button>
                        )}
                        {canEdit && ln.status === "received" && (
                          <button type="button" onClick={() => patchLine(ln.id, { status: "delivered" })} disabled={busyId === ln.id}
                            data-testid={`part-deliver-${ln.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-ink text-ink text-[10px] font-mono uppercase hover:bg-ink hover:text-white disabled:opacity-50">
                            <CheckCircle size={10} weight="bold" /> Delivered
                          </button>
                        )}
                        {canEdit && ln.status === "ordered" && (
                          <button type="button" onClick={() => deleteLine(ln.id)} disabled={busyId === ln.id}
                            data-testid={`part-delete-${ln.id}`}
                            className="p-1 border border-canvas-line hover:border-destructive text-destructive disabled:opacity-50">
                            <Trash size={10} weight="bold" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canEdit && detail.status !== "refunded" && (
        <div className="p-3 border-t border-canvas-line bg-canvas-panel/20 space-y-2" data-testid="parts-add-form">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-subtle">Add a part line</div>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4">
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Part *</label>
              <PartPicker
                value={newLine.part_code}
                onSelect={(p) => setNewLine({
                  ...newLine, part_code: p.part_code, description: p.description || "", hsn_code: p.hsn_code || "",
                  unit_price: p.unit_price || 0, tax_percent: p.tax_percent || 0,
                })}
                onTypedValue={(v) => setNewLine({ ...newLine, part_code: v })}
                testId="edit-part-picker" />
            </div>
            <div className="col-span-2">
              <F label="Qty *" type="number" value={newLine.qty} onChange={(v) => setNewLine({ ...newLine, qty: v })} />
            </div>
            <div className="col-span-3">
              <F label="Unit ₹" type="number" value={newLine.unit_price} onChange={(v) => setNewLine({ ...newLine, unit_price: v })} />
            </div>
            <div className="col-span-3">
              <button type="button" onClick={addLine} disabled={adding}
                data-testid="add-part-existing"
                className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 border border-ink text-ink text-xs font-mono uppercase hover:bg-ink hover:text-white disabled:opacity-50">
                <Plus size={10} weight="bold" /> {adding ? "…" : "Add line"}
              </button>
            </div>
            <div className="col-span-3">
              <F label="Order Date" type="date" value={newLine.order_date} onChange={(v) => setNewLine({ ...newLine, order_date: v })} />
            </div>
            <div className="col-span-2">
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Order Type</label>
              <select value={newLine.order_type || "APS"} onChange={(e) => setNewLine({ ...newLine, order_type: e.target.value })}
                data-testid="edit-part-order-type"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink">
                {ORDER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-span-4">
              <F label="Order #" value={newLine.order_number} onChange={(v) => setNewLine({ ...newLine, order_number: v })} mono />
            </div>
            <div className="col-span-3">
              <F label="Expected Delivery" type="date" value={newLine.expected_delivery_date} onChange={(v) => setNewLine({ ...newLine, expected_delivery_date: v })} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
